<template>
	<AppBottomSheet :active="active" :force-width="bottomSheetWidth" @toggling-popup="open => $emit('toggling-popup', open)">
		<div v-if="active">
			<ModalHeader
				:title="getString('self_invoice_pick_merchant')"
				:button-text="getString('self_invoice_other_merchant')"
				button-outline
				@click-button="$emit('other-merchant')" />
			<AppTable
				ref="merchants-table"
				:fetch-function="fetchMerchants"
				:filters="filters"
				:table-headings="tableHeadings"
				:total-items="total"
				:items="merchantsItems"
				initial-sort-field="transactions_count"
				@failed-fetch="showNotification('generic-fail')">
				<template #header-extra-actions>
					<AppTextInput class="merchant-search" icon-name="icon-magnify" :placeholder="getString('search')" @changed-text="v => search = v" />
				</template>
				<template #choose="{item}">
					<AppTableButtonGroup :buttons="chooseButtons" @picked-merchant="$emit('picked-merchant', item)" />
				</template>
			</AppTable>
		</div>
	</AppBottomSheet>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppTable from "@/shared/components/AppTable.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import API from "@/shared/helpers/API";
import {getLocalDateFromServer} from "@/shared/helpers/dates";
import {useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, currencyStore, getString: languageStore.getString, showNotification: notificationStore.showNotification};
	},
	name: "CardsSelfInvoiceMerchantsPicker",
	components: {AppBottomSheet, AppTable, AppTableButtonGroup, AppTextInput, ModalHeader},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "picked-merchant", "other-merchant"],
	props: {
		active: {type: Boolean, default: false},
	},
	data() {
		return {
			merchants: [],
			total: 0,
			search: "",
			filters: [
				{
					type: "date",
					key: "date",
					tableName: "merchants",
					text: this.getString("date"),
					finalValue: "",
					active: false,
				},
				{
					type: "price",
					key: "total_amount",
					text: this.getString("total"),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: "",
				},
			],
			tableHeadings: [
				{
					text: this.getString("merchant_name"),
					cols: 3,
					sortable: true,
					fieldName: "merchant_name",
				},
				{
					text: this.getString("country"),
					cols: 1,
					fieldName: "merchant_country",
					formatter: country => country || "-",
				},
				{
					text: this.getString("total"),
					cols: 1,
					sortable: true,
					fieldName: "total_amount",
					formatter: amount => this.currencyStore.readableCurrencyValue(amount, true, true, false),
				},
				{
					text: this.getString("choose"),
					cols: 1,
					fieldName: "choose",
					slotName: "choose",
				},
				{
					text: this.getString("transactions"),
					cols: 1,
					sortable: true,
					fieldName: "transactions_count",
				},
				{
					text: this.getString("self_invoice_last_transaction"),
					cols: 2,
					sortable: true,
					fieldName: "last_date",
					formatter: "date",
				},
			],
			chooseButtons: [
				{
					iconName: "icon-select-blue",
					emitName: "picked-merchant",
				},
			],
		};
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "760px";
			} else {
				return "960px";
			}
		},
		merchantsItems() {
			return this.merchants.map(merchant => ({
				...merchant,
				last_date: getLocalDateFromServer(merchant.last_date),
			}));
		},
	},
	methods: {
		fetchMerchants(filters, callback) {
			const search = this.search.trim();
			if(search) {
				filters.merchant_name = "%" + search + "%";
			}
			API.init().getCardSelfInvoiceMerchants(this.userStore.currBusinessId, filters, (response) => {
				if(response && response.result === "OK") {
					this.merchants = response.merchants;
					this.total = response.total;
					callback(true);
				} else {
					this.merchants = [];
					this.total = 0;
					callback(false, response && response.details);
				}
			});
		},
	},
	watch: {
		search(newValue, oldValue) {
			const table = this.$refs["merchants-table"];
			if(newValue !== oldValue && table) {
				// Back to the first page, like AppTable does when its own filters change
				table.currentOffset = 0;
				table.fetchData();
			}
		},
		active(isActive) {
			if(!isActive) {
				this.search = "";
			}
		},
	},
}
</script>

<style scoped lang="scss">
.merchant-search {
	max-width: 250px;
}
</style>
